import React, { useState, useEffect } from 'react';
import { motion } from "framer-motion";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaCalendarAlt, FaUserFriends } from "react-icons/fa"; // Use appropriate icons

// CustomInput for DatePicker
const CustomInput = React.forwardRef(({ value, onClick, placeholder, isError, icon: Icon }, ref) => (
  <motion.button
    type="button"
    style={{
      border: isError ? "1px solid #ef4444" : "1px solid #cbd5e1", // Adjusted border color
      color: isError ? "#ef4444" : "#475569" // Adjusted text color
    }}
    className={`w-full h-[42px] border p-2.5 rounded text-left z-10 hover:border-blue-500 transition-colors duration-300 ${
      isError ? 'bg-red-50' : 'bg-white' // Use white background
    } flex items-center gap-2 shadow-sm`}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    ref={ref}
    whileHover={{ scale: 1.01 }}
    whileTap={{ scale: 0.99 }}
  >
    {Icon && <Icon className="text-gray-500" />} {/* Display icon */}
    <span className="truncate text-sm">{value || placeholder}</span>
  </motion.button>
));

// CustomSelect for Guests
const CustomSelect = ({ value, onChange, options, placeholder, icon: Icon, name, isError }) => (
  <div className="relative">
     {Icon && <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />}
    <select
      name={name}
      value={value}
      onChange={onChange}
      style={{ border: isError ? "1px solid #ef4444" : "1px solid #cbd5e1" }} // Error styling
      className={`w-full h-[42px] border rounded bg-white text-gray-700 text-sm appearance-none hover:border-blue-500 transition-colors duration-300 focus:outline-none focus:ring-1 focus:ring-blue-500 ${Icon ? 'pl-9 pr-8' : 'px-3 pr-8'} shadow-sm ${isError ? 'bg-red-50' : ''}`}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
      </svg>
    </div>
  </div>
);

const QuickSearch = ({ initialState, onSearch }) => {
  // Initialize state based on yacht filters
  const [bookingDate, setBookingDate] = useState(initialState?.pickupDate || null);
  const [numberOfGuests, setNumberOfGuests] = useState(initialState?.guests?.toString() || '');
  const [dateError, setDateError] = useState(false);
  const [guestError, setGuestError] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  // Track if time has been selected for the date
  const [dateTimeComplete, setDateTimeComplete] = useState(!!(initialState?.pickupDate && initialState?.pickupTime));
  const [timeWarning, setTimeWarning] = useState(false);

  // Update state if initialState changes
  useEffect(() => {
    if (!initialState) return;
    
    setBookingDate(initialState.pickupDate);
    setNumberOfGuests(initialState.guests?.toString() || '');
    setDateTimeComplete(!!(initialState.pickupDate && initialState.pickupTime));
    setDateError(false); // Reset errors on filter change
    setGuestError(false);
    setTimeWarning(false);
  }, [initialState]);

  const guestOptions = Array.from({ length: 20 }, (_, i) => ({ // Example: up to 20 guests
    value: i + 1,
    label: `${i + 1} Guest${i > 0 ? 's' : ''}`
  }));

  const validateDate = (newDate) => {
    if (!newDate) return false; // No date selected
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (newDate < today) {
      setDateError(true);
      return false;
    }
    setDateError(false);
    return true;
  };

  const handleDateChange = (date) => {
    // Basic validation on change
    validateDate(date);

    const hasTime = date && (date.getHours() !== 0 || date.getMinutes() !== 0);
    setBookingDate(date);
    setDateTimeComplete(hasTime);
    setTimeWarning(date && !hasTime); // Show warning if date is set but time is not

    if (hasTime) {
      setDatePickerOpen(false); // Close picker only if time is selected
    }
  };

  const handleGuestChange = (e) => {
    setNumberOfGuests(e.target.value);
    if (e.target.value) {
        setGuestError(false); // Clear error if a value is selected
    }
  };
  const handleSearch = () => {
    // Reset errors
    setDateError(false);
    setGuestError(false);
    setTimeWarning(false);

    let isValid = true;
    if (!bookingDate) {
      setDateError(true);
      isValid = false;
    } else if (!dateTimeComplete) {
        // If date is selected but time is not
        setTimeWarning(true);
        isValid = false;
    }

    if (!numberOfGuests) {
        setGuestError(true);
        isValid = false;
    }

    // Final validation check including date being in the past
    if (bookingDate && !validateDate(bookingDate)) {
        isValid = false;
    }

    if (!isValid) return;

    // Pass updated search info back to parent with matching parameter names
    onSearch({
      bookingDate: bookingDate,
      bookingTime: `${bookingDate.getHours().toString().padStart(2, '0')}:${bookingDate.getMinutes().toString().padStart(2, '0')}`,
      numberOfGuests: parseInt(numberOfGuests, 10),
    });
  };

  return (
    // Consistent styling with FilterSidebar
    <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 Aleo">
      <h2 className="text-blue-900 text-xl font-bold mb-4">Refine Search</h2>
      <div className="space-y-4 w-full">
        {/* Date Picker */}
        <div className='w-full'>
          <label className="block text-gray-700 text-sm font-medium mb-1.5">Date & Time</label>
          <DatePicker
            selected={bookingDate}
            onChange={handleDateChange}
            customInput={<CustomInput placeholder="Select Date & Time" isError={dateError || timeWarning} icon={FaCalendarAlt} />}
            dateFormat="dd/MM/yyyy HH:mm"
            minDate={new Date()}
            placeholderText="Select Date & Time"
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={30}
            wrapperClassName='w-full'
            popperClassName="z-30" // Ensure popper is above other elements
            open={datePickerOpen}
            onClickOutside={() => {
                // Only close if date & time are complete or no date is selected
                if (dateTimeComplete || !bookingDate) {
                    setDatePickerOpen(false);
                }
            }}
            onInputClick={() => setDatePickerOpen(true)}
            onSelect={() => { // Close immediately if time is selected
                if (bookingDate && (bookingDate.getHours() !== 0 || bookingDate.getMinutes() !== 0)) {
                    setDatePickerOpen(false);
                }
            }}
          />
          {dateError && (
            <motion.p className="text-red-500 text-xs mt-1" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              Date cannot be in the past.
            </motion.p>
          )}
          {timeWarning && (
            <motion.p className="text-orange-500 text-xs mt-1" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              Please select a time.
            </motion.p>
          )}
        </div>

        {/* Guest Selector */}
        <div>
          <label className="block text-gray-700 text-sm font-medium mb-1.5">Number of Guests</label>
           <CustomSelect
              name="numberOfGuests"
              value={numberOfGuests}
              onChange={handleGuestChange}
              options={guestOptions}
              placeholder="Select Guests"
              icon={FaUserFriends}
              isError={guestError}
            />
           {guestError && (
            <motion.p className="text-red-500 text-xs mt-1" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              Please select number of guests.
            </motion.p>
          )}
        </div>

        {/* Search Button */}
        <motion.button
          onClick={handleSearch}
          className="w-full bg-gradient-to-r from-blue-500 to-blue-700 text-white font-bold py-2.5 px-4 rounded-lg transition duration-300 shadow-md hover:shadow-lg"
          whileHover={{ scale: 1.02, y: -1 }}
          whileTap={{ scale: 0.98 }}
        >
          Update Search
        </motion.button>
      </div>
    </div>
  );
};

export default QuickSearch;